import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/queries/query-keys";

export interface SocialLink {
  id: number;
  platform: string;
  label: string;
  url: string;
  visible: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateSocialLinkInput {
  platform: string;
  label: string;
  url: string;
  visible?: boolean;
  display_order?: number;
}

// Fetch visible social links (public)
export function useSocialLinks() {
  return useQuery({
    queryKey: queryKeys.socialLinks.all,
    queryFn: async (): Promise<SocialLink[]> => {
      const response = await fetch("/api/social-links");
      if (!response.ok) {
        throw new Error("Failed to fetch social links");
      }
      const data = await response.json();
      return data.socialLinks;
    },
  });
}

// Fetch all social links (admin - includes hidden)
export function useAdminSocialLinks() {
  return useQuery({
    queryKey: queryKeys.socialLinks.admin,
    queryFn: async (): Promise<SocialLink[]> => {
      const response = await fetch("/api/admin/social-links");
      if (!response.ok) {
        throw new Error("Failed to fetch social links");
      }
      const data = await response.json();
      return data.socialLinks;
    },
  });
}

// Create social link
export function useCreateSocialLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateSocialLinkInput) => {
      const response = await fetch("/api/admin/social-links", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw new Error("Failed to create social link");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.socialLinks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.socialLinks.admin });
    },
  });
}

// Update social link
export function useUpdateSocialLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: CreateSocialLinkInput & { id: number }) => {
      const response = await fetch(`/api/admin/social-links/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw new Error("Failed to update social link");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.socialLinks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.socialLinks.admin });
    },
  });
}

// Delete social link
export function useDeleteSocialLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(`/api/admin/social-links/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to delete social link");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.socialLinks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.socialLinks.admin });
    },
  });
}

// Reorder social links
export function useReorderSocialLinks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (order: number[]) => {
      const response = await fetch("/api/admin/social-links/reorder", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order }),
      });
      if (!response.ok) {
        throw new Error("Failed to reorder social links");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.socialLinks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.socialLinks.admin });
    },
  });
}
